import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@material-ui/icons/Close";
import ReactPlayer from "react-player";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import VideocamIcon from "@mui/icons-material/Videocam";
import DoNotDisturbAltOutlinedIcon from "@mui/icons-material/DoNotDisturbAltOutlined";
import CTLNotification from "application/components/CTLNotification";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import { useMaterialUIController } from "context";
import variables from "globals/variables";
import { application } from "globals/endpoints";

export default function PlaySingleFile({ row }) {
  const recordFilename = row?.original?.recordFilename;
  const uuid = row?.original?.uuid;
  const [open, setOpen] = useState(false);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  // Keep the short-lived stream token alive while the player is open. Native <audio>
  // buffers ahead and stops requesting, so a long recording (hour+) would otherwise
  // outlive a request-driven TTL and 401 on a later seek. The heartbeat extends the
  // token every 60s; it stops on close/unmount so the token self-expires when abandoned.
  const heartbeatRef = useRef(null);

  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  useEffect(() => stopHeartbeat, []);

  const isVideoFile = recordFilename?.endsWith(".mp4");
  // Secure UUID-based media URL — browser streams natively (Range/seek); the
  // short-lived vp_st cookie (set by streamToken below) authenticates the request.
  // No filesystem path is exposed in the URL.
  const fileUrl = `${variables.api.services}voiceLogger/media/${uuid}/play`;

  const handleClickOpen = async () => {
    if (!recordFilename || !uuid) {
      setNotify({
        isOpen: true,
        status: "Unable To Play File: File Not Exist",
        type: "error",
        pagename: "voice picker",
      });
      return;
    }

    try {
      // Mint + set the HttpOnly stream cookie before the native player requests the media.
      await application.get(`/voiceLogger/media/${uuid}/streamToken`);
      stopHeartbeat();
      heartbeatRef.current = setInterval(() => {
        application.post(`/voiceLogger/media/streamToken/refresh`).catch(() => {});
      }, 60000);
      setOpen(true);
    } catch (error) {
      console.error(
        "Error while playing the audio/video recording file in voice picker:",
        error
      );
      setNotify({
        isOpen: true,
        status: "Error Playing File",
        type: "error",
        pagename: "voice picker",
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    stopHeartbeat();
    // Revoke the stream token server-side so the URL cannot be replayed.
    application.delete(`/voiceLogger/media/streamToken`).catch(() => {});
  };

  const renderIcon = () => {
    if (!recordFilename) {
      return <DoNotDisturbAltOutlinedIcon color="error" />;
    }
    return isVideoFile ? (
      <VideocamIcon color="info" />
    ) : (
      <PlayCircleIcon color="success" />
    );
  };

  // ReactPlayer key toggled with open state to reset player on close/open
  return (
    <div>
      <tooltip
        title={
          !recordFilename
            ? "File Not Exist"
            : isVideoFile
            ? "Video Play"
            : "Audio Play"
        }
      >
        <IconButton
          variant="contained"
          color={recordFilename ? (isVideoFile ? "info" : "success") : "error"}
          style={{ margin: "10px" }}
          onClick={handleClickOpen}
        >
          {renderIcon()}
        </IconButton>
      </tooltip>
      <Dialog
        onClose={handleClose}
        open={open}
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
      >
        <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          Play Recording File
          <IconButton
            color="error"
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: "8px",
              top: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <ReactPlayer
            key={open ? fileUrl : "closed"} // resets player on close
            url={fileUrl}
            width={isVideoFile ? "100%" : "500px"}
            height={isVideoFile ? "100%" : "60px"}
            playing={open}
            controls={true}
          />
        </DialogContent>
      </Dialog>
      <CTLNotification notify={notify} setNotify={setNotify} />
    </div>
  );
}
